import { useState, useEffect } from 'react';

import MetricsCard from '../photographer-dashboard/components/MetricsCard';
import PerformanceChart from '../photographer-dashboard/components/PerformanceChart';
import Select from '../../components/ui/Select';
import Toast from '../../components/ui/Toast';
import { adminService } from '../../services/adminService';
import { formatNumber } from '../../utils/formatNumber';
import RevenueBreakdown from './components/RevenueBreakdown';
import PhotographerRankings from './components/PhotographerRankings';
import CustomerInsightsPanel from './components/CustomerInsightsPanel';
import CurrentActivityPanel from './components/CurrentActivityPanel';

const AdminAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [platformStats, setPlatformStats] = useState({
    totalBookings: 0,
    totalRevenue: 0,
    activePhotographers: 0,
    totalCustomers: 0
  });
  const [bookingTrends, setBookingTrends] = useState([]);
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [revenueByType, setRevenueByType] = useState([]);
  const [revenueByCity, setRevenueByCity] = useState([]);
  const [photographerPerformance, setPhotographerPerformance] = useState([]);
  const [customerInsights, setCustomerInsights] = useState({});
  const [currentActivity, setCurrentActivity] = useState({});
  const [dateFilter, setDateFilter] = useState('last_6_months');
  const [toastNotification, setToastNotification] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, [dateFilter]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [stats, bookings, revenue, typeRevenue, cityRevenue, performance, insights, activity] = await Promise.all([
        adminService?.getPlatformStats(dateFilter),
        adminService?.getBookingTrends(dateFilter),
        adminService?.getRevenueTrends(dateFilter),
        adminService?.getRevenueByType(dateFilter),
        adminService?.getRevenueByCity(dateFilter),
        adminService?.getPhotographerPerformance(),
        adminService?.getCustomerInsights(),
        adminService?.getCurrentActivity()
      ]);

      setPlatformStats(stats);
      setBookingTrends(bookings);
      setRevenueTrends(revenue);
      setRevenueByType(typeRevenue);
      setRevenueByCity(cityRevenue);
      setPhotographerPerformance(performance);
      setCustomerInsights(insights);
      setCurrentActivity(activity);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err?.message || 'فشل تحميل بيانات لوحة التحكم');
      setToastNotification({
        type: 'error',
        message: 'فشل تحميل البيانات. يرجى المحاولة مرة أخرى.'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">جاري تحميل لوحة التحكم...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                لوحة التحكم الإدارية
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                تحليلات شاملة لأداء المنصة ومقاييس الأعمال
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e?.target?.value)}
                className="w-full md:w-48"
              >
                <option value="last_6_months">آخر 6 أشهر</option>
                <option value="last_3_months">آخر 3 أشهر</option>
                <option value="last_month">الشهر الماضي</option>
                <option value="this_month">هذا الشهر</option>
              </Select>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          <MetricsCard
            title="إجمالي الحجوزات"
            value={formatNumber(platformStats?.totalBookings)}
            icon="Calendar"
            iconColor="var(--color-primary)"
            subtitle="جميع الحجوزات على المنصة"
          />
          <MetricsCard
            title="إجمالي الإيرادات"
            value={`${formatNumber(platformStats?.totalRevenue)} ريال`}
            icon="DollarSign"
            iconColor="var(--color-success)"
            subtitle="الإيرادات المحققة"
          />
          <MetricsCard
            title="المصورون النشطون"
            value={formatNumber(platformStats?.activePhotographers)}
            icon="Users"
            iconColor="var(--color-accent)"
            subtitle="مصورون مسجلون"
          />
          <MetricsCard
            title="إجمالي العملاء"
            value={formatNumber(platformStats?.totalCustomers)}
            icon="UserCheck"
            iconColor="var(--color-secondary)"
            subtitle="عملاء مسجلون"
          />
        </div>

        {/* Current Activity Panel */}
        <CurrentActivityPanel activity={currentActivity} />

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 md:mb-8">
          <PerformanceChart
            data={bookingTrends}
            type="bookings"
          />
          <PerformanceChart
            data={revenueTrends}
            type="earnings"
          />
        </div>

        {/* Revenue Breakdown */}
        <RevenueBreakdown
          revenueByType={revenueByType}
          revenueByCity={revenueByCity}
        />

        {/* Photographer Performance Rankings */}
        <PhotographerRankings photographers={photographerPerformance} />

        {/* Customer Insights */}
        <CustomerInsightsPanel insights={customerInsights} />
      </div>
      {toastNotification && (
        <Toast
          notification={toastNotification}
          onClose={() => setToastNotification(null)}
        />
      )}
    </div>
  );
};

export default AdminAnalyticsDashboard;